// 동료 모델 캐싱 시스템 - 렉 해결을 위한 최적화
// 한국어 주석: 동료 모델 캐시 관리 클래스 (webpack 환경에서도 동작하도록 window.* 참조 사용)
class CompanionModelCache {
  constructor() {
    // 모델 캐시 (타입별로 저장)
    this.modelCache = new Map();

    // 로딩 상태 추적
    this.loadingPromises = new Map();
  }

  // 모델 사전 로딩 (게임 시작 시 호출)
  async preloadAllModels() {
    Utils.log("동료 모델 사전 로딩 시작...");
    // 한국어 주석: 모듈 스코프에서는 전역 상수를 직접 참조하지 말고 window를 통해 접근
    const companionTypes = Object.keys(window.CompanionsConfig || {});
    const loadPromises = companionTypes.map((type) => this.preloadModel(type));

    await Promise.all(loadPromises);

    Utils.log("모든 동료 모델 사전 로딩 완료!");
  }

  // 특정 타입 모델 사전 로딩
  async preloadModel(companionType) {
    if (this.modelCache.has(companionType)) {
      return this.modelCache.get(companionType);
    }

    // 이미 로딩 중이면 기다리기
    if (this.loadingPromises.has(companionType)) {
      return await this.loadingPromises.get(companionType);
    }

    const config = (window.CompanionsConfig || {})[companionType];
    if (!config) {
      throw new Error(`알 수 없는 동료 타입: ${companionType}`);
    }

    // 모델 경로 검증
    const modelPath = config.MODEL_PATH;
    if (!modelPath || typeof modelPath !== "string") {
      throw new Error(
        `${companionType}의 MODEL_PATH가 유효하지 않습니다: ${modelPath}`
      );
    }

    Utils.log(`${companionType} 모델 로딩 시작: ${modelPath}`);

    const loadPromise = new Promise((resolve, reject) => {
      const loader = new THREE.GLTFLoader();

      loader.load(
        modelPath,
        (gltf) => {
          // 원본 모델 저장
          this.modelCache.set(companionType, gltf);
          this.loadingPromises.delete(companionType);

          Utils.log(`${companionType} 모델 캐시 완료`);
          resolve(gltf);
        },
        (progress) => {
          // 로딩 진행률 로그 (디버깅용)
          if (progress.lengthComputable) {
            const percentComplete = (progress.loaded / progress.total) * 100;
            Utils.log(
              `${companionType} 로딩 진행률: ${percentComplete.toFixed(1)}%`
            );
          }
        },
        (error) => {
          this.loadingPromises.delete(companionType);
          console.error(
            `${companionType} 모델 로드 실패 (경로: ${modelPath}):`,
            error
          );
          reject(
            new Error(
              `${companionType} 모델 로드 실패: ${error.message || error}`
            )
          );
        }
      );
    });

    this.loadingPromises.set(companionType, loadPromise);
    return await loadPromise;
  }

  // 캐시된 모델 복사본 생성 (인스턴싱)
  async createCompanionModel(companionType) {
    // 캐싱 활성화 (성능 최적화)
    const USE_CACHE = true; // 캐시 사용으로 성능 향상

    if (!USE_CACHE) {
      // 캐싱 없이 매번 새로 로드 (안전하지만 느림)
      return await this.loadModelDirectly(companionType);
    }

    // 캐시에서 모델 가져오기
    let gltf = this.modelCache.get(companionType);

    if (!gltf) {
      // 캐시에 없으면 로드
      gltf = await this.preloadModel(companionType);
    }

    // 완전한 모델 복사본 생성 (재귀적 복제)
    const clonedScene = this.deepCloneObject3D(gltf.scene);

    // 애니메이션도 복사
    const animations = gltf.animations;

    return {
      scene: clonedScene,
      animations: animations,
    };
  }

  // 직접 로딩 (캐싱 없이)
  async loadModelDirectly(companionType) {
    const config = (window.CompanionsConfig || {})[companionType];
    if (!config) {
      throw new Error(`알 수 없는 동료 타입: ${companionType}`);
    }

    return new Promise((resolve, reject) => {
      const loader = new THREE.GLTFLoader();

      loader.load(
        config.MODEL_PATH,
        (gltf) => {
          resolve({
            scene: gltf.scene,
            animations: gltf.animations,
          });
        },
        undefined,
        (error) => {
          console.error(`${companionType} 직접 로드 실패:`, error);
          reject(error);
        }
      );
    });
  }

  // 3D 오브젝트의 완전한 복제 (모든 메시와 재질 포함)
  deepCloneObject3D(obj) {
    // Three.js SkeletonUtils 사용이 더 안전하지만, 없는 경우 수동 복제
    if (THREE.SkeletonUtils && THREE.SkeletonUtils.clone) {
      return THREE.SkeletonUtils.clone(obj);
    }

    // 수동 복제 방식 - 더 안전한 복제
    const cloned = obj.clone(true); // 재귀적 복제 플래그

    // 모든 메시를 순회하며 재질 복제
    cloned.traverse((child) => {
      if (child.isMesh) {
        // Geometry는 개별 복제 (안전성을 위해)
        if (child.geometry) {
          child.geometry = child.geometry.clone();
        }

        // Material은 개별 복제 (색상 변경 등을 위해)
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material = child.material.map((mat) => mat.clone());
          } else {
            child.material = child.material.clone();
          }
        }

        // 메시 속성 복사
        child.castShadow = true;
        child.receiveShadow = false;
      }

      // 스켈레톤 메시의 경우 - 애니메이션을 위해 중요
      if (child.isSkinnedMesh) {
        // 스켈레톤은 공유하지 않고 각자 가져야 함
        if (child.skeleton) {
          child.skeleton = child.skeleton.clone();
        }

        // 스켈레톤 메시의 바인드 매트릭스 복사
        if (child.bindMatrix) {
          child.bindMatrix = child.bindMatrix.clone();
        }
        if (child.bindMatrixInverse) {
          child.bindMatrixInverse = child.bindMatrixInverse.clone();
        }
      }

      // 기타 중요한 속성들 복사
      if (child.userData) {
        child.userData = JSON.parse(JSON.stringify(child.userData));
      }
    });

    return cloned;
  }

  // 캐시 상태 확인
  getStatus() {
    const companionTypes = Object.keys(window.CompanionsConfig || {});
    const cached = Array.from(this.modelCache.keys());
    const loading = Array.from(this.loadingPromises.keys());

    return {
      companions: {
        total: companionTypes.length,
        cached: cached.length,
        loading: loading.length,
        cachedTypes: cached,
        loadingTypes: loading,
      },
    };
  }

  // 메모리 정리
  clearCache() {
    this.modelCache.clear();
    this.loadingPromises.clear();
    Utils.log("동료 모델 캐시가 정리되었습니다.");
  }
}

// 전역 캐시 인스턴스
window.companionModelCache = new CompanionModelCache();

// 전역으로 내보내기
if (typeof module !== "undefined" && module.exports) {
  module.exports = CompanionModelCache;
} else {
  window.CompanionModelCache = CompanionModelCache;
}

// webpack 빌드를 위해 default export 추가
export default CompanionModelCache;
